import { useSiteContent } from '@/hooks/useSiteContent';
import { useTranslation } from 'react-i18next';
import ContentState from '@/components/feature/ContentState';
import { Link } from 'react-router-dom';

interface WhyUsHeroData {
  headline: string;
  brand: string;
  subheadline: string;
  description: string;
  stats: { value: string; label: string }[];
}

export default function WhyUsHero() {
  const { data, loading, error, refresh } = useSiteContent<WhyUsHeroData>('why_us_hero');
  const { t } = useTranslation();

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="relative w-full min-h-[420px] md:min-h-[520px] flex items-center justify-center overflow-hidden pt-20">
      <img
        src="https://readdy.ai/api/search-image?query=Group%20of%20happy%20diverse%20travelers%20laughing%20together%20with%20a%20friendly%20Vietnamese%20local%20guide%20at%20a%20breathtaking%20mountain%20viewpoint%20at%20sunset%2C%20warm%20golden%20hour%20light%2C%20genuine%20joy%20and%20connection%2C%20authentic%20travel%20moment%2C%20cinematic%20photography%2C%20warm%20tones%2C%20high%20detail&width=1800&height=900&seq=why-us-hero-bg&orientation=landscape"
        alt={t('whyus.heroAlt')}
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
        <p className="text-accent-400 text-sm md:text-base tracking-wide uppercase mb-3">
          {t('whyus.eyebrow')}
        </p>
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
          {t('whyus.heroTitleA')} <span className="text-accent-400">{data.brand}</span>
        </h1>
        <p className="font-heading text-lg md:text-xl font-semibold text-white/80 mt-2">
          {data.subheadline}
        </p>
        <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto mt-4 leading-relaxed">
          {data.description}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mt-10 max-w-3xl mx-auto">
          {data.stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-heading text-2xl md:text-3xl font-bold text-accent-400">
                {stat.value}
              </div>
              <div className="text-xs md:text-sm text-white/70 mt-1 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        <nav className="mt-10 flex items-center justify-center gap-2 text-sm text-white/70">
          <Link to="/" className="hover:text-white transition-colors">{t('whyus.home')}</Link>
          <span>/</span>
          <span className="text-white">{t('whyus.whyUs')}</span>
        </nav>
      </div>
    </section>
  );
}